<template>
  <v-container fluid class="px-2 px-sm-4 pb-12">
    <div class="d-flex align-center justify-space-between flex-wrap py-4" style="gap:8px">
      <div>
        <div class="d-flex align-center mb-1">
          <v-icon color="primary" class="mr-2">mdi-account-star-outline</v-icon>
          <span class="text-h6 font-weight-bold">Growth <span class="primary--text">Admin</span></span>
        </div>
        <div class="caption grey--text">{{ range.from === 'all' ? 'All time (up to ' + range.to + ')' : range.from + ' → ' + range.to }}</div>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap:8px">
        <v-btn-toggle v-model="period" mandatory dense>
          <v-btn small value="this_month" class="text-none">This month</v-btn>
          <v-btn small value="last_month" class="text-none">Last month</v-btn>
          <v-btn small value="this_quarter" class="text-none">This quarter</v-btn>
          <v-btn small value="this_year" class="text-none">This year</v-btn>
          <v-btn small value="all_time" class="text-none">All time</v-btn>
          <v-btn small value="custom" class="text-none">Custom</v-btn>
        </v-btn-toggle>
        <template v-if="period === 'custom'">
          <v-text-field v-model="customFrom" type="date" dense outlined hide-details style="max-width:150px" />
          <v-text-field v-model="customTo" type="date" dense outlined hide-details style="max-width:150px" />
        </template>
        <v-btn small text @click="refresh"><v-icon small class="mr-1">mdi-refresh</v-icon>Refresh</v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense outlined>
      {{ error }}
      <v-btn small text class="ml-2" @click="refresh">Retry</v-btn>
    </v-alert>

    <v-row v-if="overview">
      <v-col v-for="k in kpis" :key="k.label" cols="6" md="3">
        <v-card outlined class="rounded-lg pa-3">
          <div class="caption grey--text">{{ k.label }}</div>
          <div class="text-h5 font-weight-bold">{{ k.value }}</div>
          <div v-if="k.sub" class="caption success--text">{{ k.sub }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="overview">
      <v-col cols="12" md="6"><AdminOverviewCharts :rows="overview.rows" :level-counts="overview.summary.level_counts" /></v-col>
      <v-col cols="12" md="6"><AdminAttentionPanel :items="overview.attention || []" @select="selectUser" /></v-col>
    </v-row>

    <v-row>
      <v-col cols="12"><AdminTeamTrendCard :range="range" :refresh-token="refreshToken" /></v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="8">
        <AdminRankingTable :rows="overview ? overview.rows : []" :loading="loading" :range="range" @select="selectedUser = $event" />
      </v-col>
      <v-col cols="12" lg="4">
        <AdminInsightsPanel :range="range" class="mb-4" />
        <AdminRecognitionPanel :range="range" @given="reload" />
      </v-col>
    </v-row>
    <AdminEmployeeDrawer :user="selectedUser" :range="range" @close="selectedUser = null" />
  </v-container>
</template>

<script>
import AdminRankingTable from './AdminRankingTable.vue'
import AdminEmployeeDrawer from './AdminEmployeeDrawer.vue'
import AdminRecognitionPanel from './AdminRecognitionPanel.vue'
import AdminInsightsPanel from './AdminInsightsPanel.vue'
import AdminAttentionPanel from './AdminAttentionPanel.vue'
import AdminOverviewCharts from './AdminOverviewCharts.vue'
import AdminTeamTrendCard from './AdminTeamTrendCard.vue'
import { getOverview } from '@/services/adminGrowthService'

export default {
  name: 'AdminGrowthComponent',
  components: { AdminRankingTable, AdminEmployeeDrawer, AdminRecognitionPanel, AdminInsightsPanel, AdminAttentionPanel, AdminOverviewCharts, AdminTeamTrendCard },
  data() {
    return {
      refreshToken: 0,
      period: 'this_month',
      customFrom: this.$date().startOf('month').format('YYYY-MM-DD'),
      customTo: this.$date().format('YYYY-MM-DD'),
      overview: null,
      loading: false,
      error: null,
      selectedUser: null,
    }
  },
  computed: {
    range() {
      const d = this.$date()
      const fmt = x => x.format('YYYY-MM-DD')
      if (this.period === 'last_month') {
        const m = d.subtract(1, 'month')
        return { from: fmt(m.startOf('month')), to: fmt(m.endOf('month')) }
      }
      if (this.period === 'this_quarter') {
        const qStart = d.month(Math.floor(d.month() / 3) * 3).startOf('month')
        return { from: fmt(qStart), to: fmt(qStart.add(2, 'month').endOf('month')) }
      }
      if (this.period === 'this_year') return { from: fmt(d.startOf('year')), to: fmt(d.endOf('year')) }
      // 'all' is resolved server-side to the earliest growth score
      if (this.period === 'all_time') return { from: 'all', to: fmt(d) }
      if (this.period === 'custom') return { from: this.customFrom, to: this.customTo }
      return { from: fmt(d.startOf('month')), to: fmt(d.endOf('month')) }
    },
    kpis() {
      const s = this.overview.summary
      return [
        { label: 'Avg growth score', value: s.average_growth_score },
        { label: 'Active employees', value: s.active_employees },
        { label: 'Recognitions given', value: s.recognitions_given },
        { label: 'Top climber', value: s.top_climber ? s.top_climber.name : '—', sub: s.top_climber ? `+${s.top_climber.trend} pts` : '' },
      ]
    },
  },
  watch: {
    range: { handler: 'reload', deep: true },
  },
  created() {
    this.reqId = 0 // non-reactive request counter (guards out-of-order responses)
  },
  mounted() {
    this.reload()
  },
  methods: {
    refresh() {
      this.refreshToken++
      this.reload()
    },
    selectUser(userId) {
      const row = this.overview && this.overview.rows.find(r => r.user_id === userId)
      if (row) this.selectedUser = row
    },
    async reload() {
      if (this.period === 'custom' && (!this.customFrom || !this.customTo)) return
      const id = ++this.reqId
      if (this.period === 'custom' && this.customFrom > this.customTo) {
        this.error = 'From must be on or before To'
        this.overview = null
        this.loading = false
        return
      }
      this.loading = true
      this.error = null
      try {
        const data = await getOverview(this.range.from, this.range.to)
        if (id !== this.reqId) return
        this.overview = data
      } catch (e) {
        if (id !== this.reqId) return
        this.error = e.message
        this.overview = null
      } finally {
        if (id === this.reqId) this.loading = false
      }
    },
  },
}
</script>
